// "Build from Documents" dashboard card (s98). A permanent tool that prompts planners to
// upload their will / other documents and auto-populate via the fan-out pipeline. Always
// shown (no plan-progress threshold, no permanent dismiss) except for a session-level
// "Not right now". Design: build-from-documents.md.

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileUp } from "lucide-react";
import { shouldShowBuildFromDocumentsCard } from "@/lib/build-from-documents";
import { DOCUMENT_ACCEPT_CARD_LINE } from "@/lib/upload-accept";
import { FONT, SAGE, WARM } from "@shared/brand";

// Session-scoped dismiss: module-level so "Not right now" survives in-session SPA
// navigation (the card unmounts when you leave the dashboard) but resets on a full
// page reload — matching the design ("hides for the session; reappears next login").
let sessionDismissed = false;

interface BuildFromDocumentsCardProps {
  /** Opens the batch upload modal. */
  onUploadClick: () => void;
}

export function BuildFromDocumentsCard({ onUploadClick }: BuildFromDocumentsCardProps) {
  const [dismissed, setDismissed] = useState(sessionDismissed);

  if (!shouldShowBuildFromDocumentsCard(dismissed)) return null;

  const dismiss = () => {
    sessionDismissed = true;
    setDismissed(true);
  };

  return (
    <Card className="border-l-4" style={{ borderLeftColor: SAGE[600] }} data-testid="build-from-documents-card">
      <CardContent className="p-5 flex items-start gap-4">
        <div className="rounded-full p-2.5 shrink-0" style={{ backgroundColor: SAGE[50] }}>
          <FileUp className="h-5 w-5" style={{ color: SAGE[600] }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold" style={{ fontFamily: FONT.serif }}>
            Have your will or other documents handy?
          </h3>
          <p className="text-sm mt-1" style={{ color: "#7A7367" }}>
            Upload them and we'll fill in your estate plan for you. One document can populate up to 12 sections.
          </p>
          <p className="text-xs italic mt-1" style={{ color: WARM.taupe }}>
            Supports wills, insurance policies, bank statements, leases, and more.
          </p>
          <p className="text-xs mt-1" style={{ color: WARM.taupe }}>
            {DOCUMENT_ACCEPT_CARD_LINE}
          </p>
          <div className="flex items-center gap-4 mt-3">
            <Button onClick={onUploadClick} className="gap-2" data-testid="button-build-from-documents-upload">
              <FileUp className="h-4 w-4" />
              Upload documents
            </Button>
            <button
              onClick={dismiss}
              className="text-sm text-muted-foreground hover:text-foreground underline"
              data-testid="button-build-from-documents-dismiss"
            >
              Not right now
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
